// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useMemo } from 'react';
import type { TimeSeriesData } from '../../lib/models';
import './PerCycleTrace.css';

export interface PerCycleTraceProps {
  data: TimeSeriesData[];
}

interface TraceTotals {
  bgDeathsNovac: number | undefined;
  bgDeathsVac: number | undefined;
  deathsNovac: number | undefined;
  deathsVac: number | undefined;
  baselineCases: number | undefined;
  withVaccineCases: number | undefined;
  prevented: number | undefined;
  qalysNoVac: number | undefined;
  qalysVac: number | undefined;
  deltaQaly: number | undefined;
  qalysNoVacDisc: number | undefined;
  qalysVacDisc: number | undefined;
  deltaQalyDisc: number | undefined;
}

const sumField = (
  rows: TimeSeriesData[],
  pick: (r: TimeSeriesData) => number | undefined
): number | undefined => {
  let total = 0;
  let any = false;
  for (const r of rows) {
    const v = pick(r);
    if (typeof v === 'number' && Number.isFinite(v)) {
      total += v;
      any = true;
    }
  }
  return any ? total : undefined;
};

const computeTotals = (rows: TimeSeriesData[]): TraceTotals => {
  const baselineCases = sumField(rows, (r) => r.baselineCases);
  const withVaccineCases = sumField(rows, (r) => r.withVaccineCases);
  const qalysNoVac = sumField(rows, (r) => r.qalysWithoutVaccine);
  const qalysVac = sumField(rows, (r) => r.qalysWithVaccine);
  const qalysNoVacDisc = sumField(rows, (r) => r.qalysWithoutVaccineDiscounted);
  const qalysVacDisc = sumField(rows, (r) => r.qalysWithVaccineDiscounted);
  return {
    bgDeathsNovac: sumField(rows, (r) => r.bgDeathsNovac),
    bgDeathsVac: sumField(rows, (r) => r.bgDeathsVac),
    deathsNovac: sumField(rows, (r) => r.deathsNovac),
    deathsVac: sumField(rows, (r) => r.deathsVac),
    baselineCases,
    withVaccineCases,
    prevented:
      baselineCases !== undefined && withVaccineCases !== undefined
        ? baselineCases - withVaccineCases
        : undefined,
    qalysNoVac,
    qalysVac,
    deltaQaly:
      qalysVac !== undefined && qalysNoVac !== undefined
        ? qalysVac - qalysNoVac
        : undefined,
    qalysNoVacDisc,
    qalysVacDisc,
    deltaQalyDisc:
      qalysVacDisc !== undefined && qalysNoVacDisc !== undefined
        ? qalysVacDisc - qalysNoVacDisc
        : undefined,
  };
};

// Discounting is "active" when at least one cycle has a factor < 1. Cycle 1 is
// always 1 by start-of-year convention, so we look across the whole series.
const hasActiveDiscounting = (rows: TimeSeriesData[]): boolean =>
  rows.some(
    (r) => typeof r.discountFactor === 'number' && r.discountFactor < 1
  );

const intFmt = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });
const oneDecimalFmt = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});
const twoDecimalFmt = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});
const threeDecimalFmt = new Intl.NumberFormat(undefined, {
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
});

// Plumber serializes NA_real_ as the string "NA" (na = "string"), so guard
// against any non-finite-number value reaching Intl.NumberFormat — it would
// otherwise coerce "NA" to NaN and render locale-specific "NaN" labels
// (e.g. "epäluku" in Finnish).
const isMissing = (v: unknown): boolean =>
  typeof v !== 'number' || !Number.isFinite(v);

const formatInt = (v: number | undefined): string =>
  isMissing(v) ? '—' : intFmt.format(v as number);
const formatOneDecimal = (v: number | undefined): string =>
  isMissing(v) ? '—' : oneDecimalFmt.format(v as number);
const formatTwoDecimal = (v: number | undefined): string =>
  isMissing(v) ? '—' : twoDecimalFmt.format(v as number);
const formatThreeDecimal = (v: number | undefined): string =>
  isMissing(v) ? '—' : threeDecimalFmt.format(v as number);
const formatVE = (v: number | undefined): string =>
  isMissing(v) ? '—' : `${((v as number) * 100).toFixed(1)}%`;
const formatRatePct = (v: number | undefined): string =>
  isMissing(v) ? '—' : `${((v as number) * 100).toFixed(2)}%`;

const buildCsv = (
  rows: TimeSeriesData[],
  totals: TraceTotals,
  includeDiscounted: boolean
): string => {
  const baseHeader = [
    'Cycle',
    'VE applied',
    'Bg mortality (rate)',
    'Bg utility',
    'Alive (no-vac)',
    'Alive (vac)',
    'BG deaths (no-vac)',
    'BG deaths (vac)',
    'Total deaths (no-vac)',
    'Total deaths (vac)',
    'Cases (no-vac)',
    'Cases (vac)',
    'Cases prevented',
    'QALYs (no-vac, undiscounted)',
    'QALYs (vac, undiscounted)',
    'Delta QALY (undiscounted)',
  ];
  const discountedHeader = [
    'Discount factor',
    'QALYs (no-vac, discounted)',
    'QALYs (vac, discounted)',
    'Delta QALY (discounted)',
  ];
  const header = (
    includeDiscounted ? [...baseHeader, ...discountedHeader] : baseHeader
  ).join(',');
  const body = rows.map((row) => {
    const prevented =
      row.baselineCases !== undefined && row.withVaccineCases !== undefined
        ? row.baselineCases - row.withVaccineCases
        : '';
    const deltaQaly =
      row.qalysWithVaccine !== undefined && row.qalysWithoutVaccine !== undefined
        ? row.qalysWithVaccine - row.qalysWithoutVaccine
        : '';
    const baseCols = [
      row.year,
      row.vaccineEfficacy ?? '',
      row.backgroundMortality ?? '',
      row.backgroundUtility ?? '',
      row.aliveNovac ?? '',
      row.aliveVac ?? '',
      row.bgDeathsNovac ?? '',
      row.bgDeathsVac ?? '',
      row.deathsNovac ?? '',
      row.deathsVac ?? '',
      row.baselineCases ?? '',
      row.withVaccineCases ?? '',
      prevented,
      row.qalysWithoutVaccine ?? '',
      row.qalysWithVaccine ?? '',
      deltaQaly,
    ];
    if (!includeDiscounted) return baseCols.join(',');
    const deltaQalyDisc =
      row.qalysWithVaccineDiscounted !== undefined &&
      row.qalysWithoutVaccineDiscounted !== undefined
        ? row.qalysWithVaccineDiscounted - row.qalysWithoutVaccineDiscounted
        : '';
    return [
      ...baseCols,
      row.discountFactor ?? '',
      row.qalysWithoutVaccineDiscounted ?? '',
      row.qalysWithVaccineDiscounted ?? '',
      deltaQalyDisc,
    ].join(',');
  });
  const baseTotal = [
    'Total',
    '',
    '',
    '',
    '',
    '',
    totals.bgDeathsNovac ?? '',
    totals.bgDeathsVac ?? '',
    totals.deathsNovac ?? '',
    totals.deathsVac ?? '',
    totals.baselineCases ?? '',
    totals.withVaccineCases ?? '',
    totals.prevented ?? '',
    totals.qalysNoVac ?? '',
    totals.qalysVac ?? '',
    totals.deltaQaly ?? '',
  ];
  const totalRow = (
    includeDiscounted
      ? [
          ...baseTotal,
          '',
          totals.qalysNoVacDisc ?? '',
          totals.qalysVacDisc ?? '',
          totals.deltaQalyDisc ?? '',
        ]
      : baseTotal
  ).join(',');
  return [header, ...body, totalRow].join('\n');
};

const downloadCsv = (filename: string, content: string): void => {
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const PerCycleTrace: React.FC<PerCycleTraceProps> = ({ data }) => {
  const hasAlive = data.some(
    (row) => row.aliveVac !== undefined || row.aliveNovac !== undefined
  );
  const hasDiscounted = useMemo(() => hasActiveDiscounting(data), [data]);
  const totals = useMemo(() => computeTotals(data), [data]);

  if (!data.length || !hasAlive) {
    return (
      <div className="per-cycle-trace">
        <p className="per-cycle-trace__placeholder">
          Per-cycle trace not available for this analysis. Run a multi-year computation
          to populate cohort sizes and cycle-level vaccine efficacy.
        </p>
      </div>
    );
  }

  return (
    <div className="per-cycle-trace">
      <div className="per-cycle-trace__header">
        <p className="per-cycle-trace__hint">
          Per-cycle Markov outputs for validation. Counts and rates are never
          discounted.{' '}
          {hasDiscounted ? (
            <>
              QALYs are shown both <strong>undiscounted</strong> (raw per-cycle
              accruals) and <strong>discounted</strong>; discounted totals match
              the headline cumulative ΔQALY by construction.
            </>
          ) : (
            <>
              QALYs in this table are <strong>undiscounted</strong> raw per-cycle
              accruals (no discounting is applied at the configured rate or
              horizon).
            </>
          )}{' '}
          Use the CSV export to diff against published reports or external models.
        </p>
        <button
          type="button"
          className="per-cycle-trace__csv-button"
          onClick={() =>
            downloadCsv(
              'per-cycle-trace.csv',
              buildCsv(data, totals, hasDiscounted)
            )
          }
        >
          Download CSV
        </button>
      </div>

      <div className="per-cycle-trace__scroll">
        <table className="per-cycle-trace__table">
          <colgroup>
            <col className="per-cycle-trace__col--cycle" />
          </colgroup>
          <colgroup className="per-cycle-trace__group per-cycle-trace__group--inputs">
            <col />
            <col />
            <col />
          </colgroup>
          <colgroup className="per-cycle-trace__group per-cycle-trace__group--cohort">
            <col />
            <col />
          </colgroup>
          <colgroup className="per-cycle-trace__group per-cycle-trace__group--deaths">
            <col />
            <col />
            <col />
            <col />
          </colgroup>
          <colgroup className="per-cycle-trace__group per-cycle-trace__group--cases">
            <col />
            <col />
            <col className="per-cycle-trace__col--delta" />
          </colgroup>
          <colgroup className="per-cycle-trace__group per-cycle-trace__group--qalys">
            <col />
            <col />
            <col className="per-cycle-trace__col--delta" />
          </colgroup>
          {hasDiscounted && (
            <colgroup className="per-cycle-trace__group per-cycle-trace__group--qalys-disc">
              <col />
              <col />
              <col />
              <col className="per-cycle-trace__col--delta" />
            </colgroup>
          )}
          <thead>
            <tr className="per-cycle-trace__group-row">
              <th aria-hidden="true" />
              <th colSpan={3} scope="colgroup">
                Inputs
                <span
                  className="per-cycle-trace__badge per-cycle-trace__badge--neutral"
                  title="Per-cycle model inputs and rates — discounting does not apply"
                >
                  rates
                </span>
              </th>
              <th colSpan={2} scope="colgroup">
                Cohort alive
                <span
                  className="per-cycle-trace__badge per-cycle-trace__badge--neutral"
                  title="Cohort headcount — discounting does not apply"
                >
                  counts
                </span>
              </th>
              <th colSpan={4} scope="colgroup">
                Deaths
                <span
                  className="per-cycle-trace__badge per-cycle-trace__badge--neutral"
                  title="Event counts — discounting does not apply"
                >
                  counts
                </span>
              </th>
              <th colSpan={3} scope="colgroup">
                Cases
                <span
                  className="per-cycle-trace__badge per-cycle-trace__badge--neutral"
                  title="Event counts — discounting does not apply"
                >
                  counts
                </span>
              </th>
              <th colSpan={3} scope="colgroup">
                QALYs (utility)
                <span
                  className="per-cycle-trace__badge per-cycle-trace__badge--undiscounted"
                  title="Raw per-cycle QALY accruals; the headline summary applies the configured discount rate"
                >
                  undiscounted
                </span>
              </th>
              {hasDiscounted && (
                <th colSpan={4} scope="colgroup">
                  QALYs (discounted)
                  <span
                    className="per-cycle-trace__badge per-cycle-trace__badge--discounted"
                    title="Per-cycle QALYs multiplied by the start-of-year discount factor; sums match the headline cumulative ΔQALY"
                  >
                    discounted
                  </span>
                </th>
              )}
            </tr>
            <tr className="per-cycle-trace__head-row">
              <th scope="col">Cycle</th>
              <th scope="col" title="Per-cycle vaccine efficacy applied">VE</th>
              <th
                scope="col"
                title="Per-cycle background all-cause mortality rate (p_death_bg). Combined with disease mortality via competing risks: p_death = 1 − (1−p_bg)·(1−p_inf·cfr)."
              >
                Bg mortality
              </th>
              <th
                scope="col"
                title="Per-cycle general-population HRQoL multiplier (bg_util) applied to healthy-state utility (qaly = bg_util · utility_healthy − per-case decrements). Resolved by age from the configured background_utility_source registry. Shared across arms, so it largely cancels in ΔQALY. Y1 uses the value at starting_age, applied to the decision-tree healthy-state utility via an additive correction; per-case decrements are not scaled."
              >
                Bg utility
              </th>
              <th scope="col">No-vac</th>
              <th scope="col">Vac</th>
              <th scope="col" title="Bg deaths under independent risks (alive × p_bg). Approximate attribution; total deaths use the competing-risks formula.">BG (no-vac)</th>
              <th scope="col" title="Bg deaths under independent risks (alive × p_bg). Approximate attribution; total deaths use the competing-risks formula.">BG (vac)</th>
              <th scope="col" title="Total deaths in this cycle (competing risks: bg + disease).">Total (no-vac)</th>
              <th scope="col" title="Total deaths in this cycle (competing risks: bg + disease).">Total (vac)</th>
              <th scope="col">No-vac</th>
              <th scope="col">Vac</th>
              <th scope="col" title="Cases (no-vac) − Cases (vac)">Δ prevented</th>
              <th
                scope="col"
                title="Total un-discounted QALYs accrued in the no-vaccine arm during this cycle"
              >
                No-vac
              </th>
              <th
                scope="col"
                title="Total un-discounted QALYs accrued in the vaccine arm during this cycle"
              >
                Vac
              </th>
              <th
                scope="col"
                title="Per-cycle incremental QALYs (vac − no-vac); sums approximate the headline ΔQALY before discounting"
              >
                ΔQALY
              </th>
              {hasDiscounted && (
                <>
                  <th
                    scope="col"
                    title="Start-of-year discount factor 1/(1+r)^(year-1); cycle 1 is always 1"
                  >
                    Factor
                  </th>
                  <th
                    scope="col"
                    title="Discounted QALYs (no-vac) — undiscounted × factor"
                  >
                    No-vac
                  </th>
                  <th
                    scope="col"
                    title="Discounted QALYs (vac) — undiscounted × factor"
                  >
                    Vac
                  </th>
                  <th
                    scope="col"
                    title="Per-cycle discounted incremental QALYs (vac − no-vac); sums to the headline cumulative ΔQALY"
                  >
                    ΔQALY
                  </th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {data.map((row) => {
              const prevented =
                row.baselineCases !== undefined && row.withVaccineCases !== undefined
                  ? row.baselineCases - row.withVaccineCases
                  : undefined;
              const deltaQaly =
                row.qalysWithVaccine !== undefined && row.qalysWithoutVaccine !== undefined
                  ? row.qalysWithVaccine - row.qalysWithoutVaccine
                  : undefined;
              return (
                <tr key={String(row.year)}>
                  <th scope="row">{row.year}</th>
                  <td>{formatVE(row.vaccineEfficacy)}</td>
                  <td>{formatRatePct(row.backgroundMortality)}</td>
                  <td>{formatThreeDecimal(row.backgroundUtility)}</td>
                  <td>{formatInt(row.aliveNovac)}</td>
                  <td>{formatInt(row.aliveVac)}</td>
                  <td>{formatOneDecimal(row.bgDeathsNovac)}</td>
                  <td>{formatOneDecimal(row.bgDeathsVac)}</td>
                  <td>{formatOneDecimal(row.deathsNovac)}</td>
                  <td>{formatOneDecimal(row.deathsVac)}</td>
                  <td>{formatOneDecimal(row.baselineCases)}</td>
                  <td>{formatOneDecimal(row.withVaccineCases)}</td>
                  <td
                    className="per-cycle-trace__cell--delta"
                    title={
                      prevented === undefined
                        ? undefined
                        : `Cases prevented = Cases(no-vac) − Cases(vac) = ${formatOneDecimal(row.baselineCases)} − ${formatOneDecimal(row.withVaccineCases)} = ${formatOneDecimal(prevented)}`
                    }
                  >
                    {formatOneDecimal(prevented)}
                  </td>
                  <td>{formatTwoDecimal(row.qalysWithoutVaccine)}</td>
                  <td>{formatTwoDecimal(row.qalysWithVaccine)}</td>
                  <td
                    className="per-cycle-trace__cell--delta"
                    title={
                      deltaQaly === undefined
                        ? undefined
                        : `ΔQALY = QALYs(vac) − QALYs(no-vac) = ${formatTwoDecimal(row.qalysWithVaccine)} − ${formatTwoDecimal(row.qalysWithoutVaccine)} = ${formatTwoDecimal(deltaQaly)}`
                    }
                  >
                    {formatTwoDecimal(deltaQaly)}
                  </td>
                  {hasDiscounted && (() => {
                    const deltaQalyDisc =
                      row.qalysWithVaccineDiscounted !== undefined &&
                      row.qalysWithoutVaccineDiscounted !== undefined
                        ? row.qalysWithVaccineDiscounted -
                          row.qalysWithoutVaccineDiscounted
                        : undefined;
                    return (
                      <>
                        <td>{formatThreeDecimal(row.discountFactor)}</td>
                        <td>
                          {formatTwoDecimal(row.qalysWithoutVaccineDiscounted)}
                        </td>
                        <td>
                          {formatTwoDecimal(row.qalysWithVaccineDiscounted)}
                        </td>
                        <td
                          className="per-cycle-trace__cell--delta"
                          title={
                            deltaQalyDisc === undefined
                              ? undefined
                              : `ΔQALY (discounted) = ${formatTwoDecimal(row.qalysWithVaccineDiscounted)} − ${formatTwoDecimal(row.qalysWithoutVaccineDiscounted)} = ${formatTwoDecimal(deltaQalyDisc)}`
                          }
                        >
                          {formatTwoDecimal(deltaQalyDisc)}
                        </td>
                      </>
                    );
                  })()}
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="per-cycle-trace__total-row">
              <th scope="row">Total</th>
              <td aria-hidden="true" />
              <td aria-hidden="true" />
              <td aria-hidden="true" />
              <td aria-hidden="true" />
              <td aria-hidden="true" />
              <td>{formatOneDecimal(totals.bgDeathsNovac)}</td>
              <td>{formatOneDecimal(totals.bgDeathsVac)}</td>
              <td>{formatOneDecimal(totals.deathsNovac)}</td>
              <td>{formatOneDecimal(totals.deathsVac)}</td>
              <td>{formatOneDecimal(totals.baselineCases)}</td>
              <td>{formatOneDecimal(totals.withVaccineCases)}</td>
              <td className="per-cycle-trace__cell--delta">
                {formatOneDecimal(totals.prevented)}
              </td>
              <td>{formatTwoDecimal(totals.qalysNoVac)}</td>
              <td>{formatTwoDecimal(totals.qalysVac)}</td>
              <td className="per-cycle-trace__cell--delta">
                {formatTwoDecimal(totals.deltaQaly)}
              </td>
              {hasDiscounted && (
                <>
                  <td aria-hidden="true" />
                  <td>{formatTwoDecimal(totals.qalysNoVacDisc)}</td>
                  <td>{formatTwoDecimal(totals.qalysVacDisc)}</td>
                  <td className="per-cycle-trace__cell--delta">
                    {formatTwoDecimal(totals.deltaQalyDisc)}
                  </td>
                </>
              )}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
};

export default PerCycleTrace;
